<template>
  <div class="card mb-3">
    <div class="card-header">
      <div class="header-icon">
        <i class="fa-solid fa-bullseye"></i>
      </div>
      <div class="header-content">
        <h3 class="card-title">Create Savings Goal</h3>
        <p class="card-subtitle">Set a target amount and timeframe</p>
      </div>
    </div>

    <div class="card-body">
      <form @submit.prevent="handleSubmit">
        <!-- Goal Name Input -->
        <div class="mb-3">
          <label for="goalName" class="form-label fw-semibold">
            <i class="fa-solid fa-tag me-1"></i>Goal Name
          </label>
          <input
            id="goalName"
            v-model="formData.name"
            type="text"
            class="form-control form-control-lg"
            :class="{ 'is-invalid': errors.name }"
            placeholder="e.g., Emergency Fund, Vacation, New Car"
            maxlength="50"
            @input="validateField('name')"
            @blur="validateField('name')"
          />
          <div v-if="errors.name" class="invalid-feedback">
            {{ errors.name }}
          </div>
          <small class="text-muted">
            <i class="fa-solid fa-info-circle me-1"></i>
            {{ formData.name.length }}/50 characters
          </small>
        </div>

        <!-- Target Amount Input -->
        <div class="mb-3">
          <label for="targetAmount" class="form-label fw-semibold">
            <i class="fa-solid fa-dollar-sign me-1"></i>Target Amount
          </label>
          <div class="input-group input-group-lg">
            <span class="input-group-text currency-symbol">$</span>
            <input
              id="targetAmount"
              v-model.number="formData.targetAmount"
              type="number"
              class="form-control"
              :class="{ 'is-invalid': errors.targetAmount }"
              step="0.01"
              min="0.01"
              placeholder="0.00"
              @input="validateField('targetAmount')"
              @blur="validateField('targetAmount')"
            />
          </div>
          <div v-if="errors.targetAmount" class="invalid-feedback d-block">
            {{ errors.targetAmount }}
          </div>
        </div>

        <!-- Duration Input -->
        <div class="mb-3">
          <label for="duration" class="form-label fw-semibold">
            <i class="fa-solid fa-calendar-days me-1"></i>Duration (Days)
          </label>
          <input
            id="duration"
            v-model.number="formData.duration"
            type="number"
            class="form-control form-control-lg"
            :class="{ 'is-invalid': errors.duration }"
            min="1"
            max="365"
            placeholder="30"
            @input="validateField('duration')"
            @blur="validateField('duration')"
          />
          <div v-if="errors.duration" class="invalid-feedback">
            {{ errors.duration }}
          </div>
          <small class="text-muted">
            <i class="fa-solid fa-info-circle me-1"></i>
            Between 1 and 365 days
          </small>
        </div>

        <!-- Real-time Daily Allocation Display -->
        <div v-if="isValidForCalculation" class="daily-allocation-preview mb-4">
          <div class="allocation-card">
            <div class="allocation-header">
              <i class="fa-solid fa-calculator me-2"></i>
              <span class="allocation-title">Daily Allocation</span>
            </div>
            <div class="allocation-amount">
              ${{ calculatedDailyAllocation.toFixed(2) }}
            </div>
            <div class="allocation-subtitle">
              per day for {{ formData.duration }} days
            </div>
          </div>
        </div>

        <!-- Form Actions -->
        <div class="form-actions">
          <button
            type="submit"
            class="btn btn-primary btn-lg w-100"
            :disabled="!isFormValid || isSubmitting"
          >
            <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            <i v-else class="fa-solid fa-plus me-2"></i>
            <span v-if="isSubmitting">Creating Goal...</span>
            <span v-else>Create Savings Goal</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { useSavingsGoals } from '../composables/useSavingsGoals.js';
import { useErrorModal } from '../composables/useErrorModal.js';

export default {
  name: 'GoalForm',
  emits: ['goal-created'],
  setup() {
    const { createGoal, error: savingsError } = useSavingsGoals();
    const { showError } = useErrorModal();
    
    return {
      createGoal,
      savingsError,
      showError
    };
  },
  data() {
    return {
      formData: {
        name: '',
        targetAmount: null,
        duration: null
      },
      errors: {
        name: null,
        targetAmount: null,
        duration: null
      },
      isSubmitting: false,
      hasAttemptedSubmit: false
    };
  },
  computed: {
    // Check if form has valid data for daily allocation calculation
    isValidForCalculation() {
      return (
        this.formData.targetAmount > 0 &&
        this.formData.duration > 0 &&
        this.formData.duration <= 365 &&
        !this.errors.targetAmount &&
        !this.errors.duration
      );
    },

    // Calculate daily allocation in real-time (Requirement 1.5)
    calculatedDailyAllocation() {
      if (!this.isValidForCalculation) return 0;
      return this.formData.targetAmount / this.formData.duration;
    },

    // Check if entire form is valid
    isFormValid() {
      return (
        this.formData.name.trim().length > 0 &&
        this.formData.targetAmount > 0 &&
        this.formData.duration > 0 &&
        !this.errors.name &&
        !this.errors.targetAmount &&
        !this.errors.duration
      );
    }
  },
  methods: {
    // Validate individual form fields (Requirements 1.2, 1.3, 1.4)
    validateField(fieldName) {
      switch (fieldName) {
        case 'name':
          this.validateName();
          break;
        case 'targetAmount':
          this.validateTargetAmount();
          break;
        case 'duration':
          this.validateDuration();
          break;
      }
    },

    // Requirement 1.2: Goal name validation
    validateName() {
      const name = this.formData.name;
      
      if (!name || typeof name !== 'string') {
        this.errors.name = 'Goal name is required';
        return false;
      }
      
      if (name.trim().length === 0) {
        this.errors.name = 'Goal name cannot be empty';
        return false;
      }
      
      if (name.length > 50) {
        this.errors.name = 'Goal name must be under 50 characters';
        return false;
      }
      
      this.errors.name = null;
      return true;
    },

    // Requirement 1.3: Target amount validation
    validateTargetAmount() {
      const amount = this.formData.targetAmount;
      
      if (amount === null || amount === undefined || amount === '') {
        this.errors.targetAmount = 'Target amount is required';
        return false;
      }
      
      if (typeof amount !== 'number' || isNaN(amount)) {
        this.errors.targetAmount = 'Target amount must be a valid number';
        return false;
      }
      
      if (amount <= 0) {
        this.errors.targetAmount = 'Target amount must be greater than zero';
        return false;
      }
      
      this.errors.targetAmount = null;
      return true;
    },

    // Requirement 1.4: Duration validation
    validateDuration() {
      const duration = this.formData.duration;
      
      if (duration === null || duration === undefined || duration === '') {
        this.errors.duration = 'Duration is required';
        return false;
      }
      
      if (!Number.isInteger(duration)) {
        this.errors.duration = 'Duration must be a whole number';
        return false;
      }
      
      if (duration < 1) {
        this.errors.duration = 'Duration must be at least 1 day';
        return false;
      }
      
      if (duration > 365) {
        this.errors.duration = 'Duration cannot exceed 365 days';
        return false;
      }
      
      this.errors.duration = null;
      return true;
    },

    // Validate entire form
    validateForm() {
      const nameValid = this.validateName();
      const amountValid = this.validateTargetAmount();
      const durationValid = this.validateDuration();
      
      return nameValid && amountValid && durationValid;
    },

    // Handle form submission (Requirement 1.1)
    async handleSubmit() {
      this.hasAttemptedSubmit = true;
      
      // Validate all fields
      if (!this.validateForm()) {
        return;
      }
      
      this.isSubmitting = true;
      
      try {
        // Create the goal using the composable
        await this.createGoal({
          name: this.formData.name.trim(),
          targetAmount: this.formData.targetAmount,
          duration: this.formData.duration
        });
        
        // Emit success event to parent component
        this.$emit('goal-created', {
          name: this.formData.name.trim(),
          targetAmount: this.formData.targetAmount,
          duration: this.formData.duration,
          dailyAllocation: this.calculatedDailyAllocation
        });
        
        // Reset form after successful creation
        this.resetForm();
        
      } catch (error) {
        console.error('Failed to create goal:', error);
        
        // Show user-friendly error message
        this.showError(
          'Failed to Create Goal',
          error.message || 'An unexpected error occurred while creating your savings goal. Please try again.'
        );
      } finally {
        this.isSubmitting = false;
      }
    },

    // Reset form to initial state
    resetForm() {
      this.formData = {
        name: '',
        targetAmount: null,
        duration: null
      };
      this.errors = {
        name: null,
        targetAmount: null,
        duration: null
      };
      this.hasAttemptedSubmit = false;
    }
  }
};
</script>

<style scoped>
/* Form styling following established patterns */
.form-label {
  color: var(--light-text);
  font-size: 0.95rem;
  margin-bottom: 0.5rem;
}

.form-control {
  border-radius: 0.5rem;
  border: 2px solid var(--light-border);
  transition: all 0.2s ease;
  background-color: var(--light-input-bg);
  color: var(--light-text);
}

.form-control:focus {
  background-color: var(--light-input-bg);
  border-color: var(--light-text);
  color: var(--light-text);
  box-shadow: none;
}

.form-control::placeholder {
  color: var(--light-text-muted);
}

.form-control.is-invalid {
  border-color: var(--light-danger);
}

.invalid-feedback {
  color: var(--light-danger);
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

/* Currency symbol styling */
.currency-symbol {
  background-color: var(--bg-secondary) !important;
  color: var(--text-primary) !important;
  border-top: 2px solid var(--border-color) !important;
  border-left: 2px solid var(--border-color) !important;
  border-bottom: 2px solid var(--border-color) !important;
}

/* Daily allocation preview card */
.daily-allocation-preview {
  background: var(--nested-container-bg);
  border-radius: 8px;
  padding: 1rem;
  border: 2px solid var(--light-border);
}

.allocation-card {
  text-align: center;
}

.allocation-header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.5rem;
}

.allocation-title {
  color: var(--light-text);
  font-weight: 600;
  font-size: 0.9rem;
}

.allocation-amount {
  color: var(--light-success);
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.allocation-subtitle {
  color: var(--light-text-muted);
  font-size: 0.85rem;
}

/* Form actions */
.form-actions {
  border-top: 1px solid var(--light-border);
  padding-top: 1rem;
  margin-top: 1rem;
}

.btn-primary {
  background-color: #36A2EB;
  border-color: #36A2EB;
  min-height: 50px;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #2e8bc7;
  border-color: #2e8bc7;
}

.btn-primary:disabled {
  background-color: var(--light-text-muted);
  border-color: var(--light-text-muted);
  opacity: 0.6;
}

/* Character counter and help text */
small.text-muted {
  color: var(--light-text-muted);
  font-size: 0.8rem;
  margin-top: 0.25rem;
  display: block;
}

/* Responsive design */
@media (max-width: 768px) {
  .allocation-amount {
    font-size: 1.75rem;
  }
  
  .daily-allocation-preview {
    padding: 0.75rem;
  }
}

/* Header icon styling */
.header-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--light-text);
  font-size: 1.25rem;
  flex-shrink: 0;
  border: 1px solid var(--light-text);
}
</style>